const HEAD_REQUEST_TIMEOUT_MS = 3000

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v'])

const cache = new Map<string, boolean>()

export function clearIsVideoUrlCache(): void {
  cache.clear()
}

export function hasVideoExtension(url: string): boolean {
  try {
    const { pathname } = new URL(url)
    const match = pathname.match(/\.([a-z0-9]+)$/i)
    return match ? VIDEO_EXTENSIONS.has(match[1].toLowerCase()) : false
  } catch {
    return false
  }
}

/**
 * This function checks if a URL is a video URL by checking if the URL has a video extension
 * or if the URL is a video URL by making a HEAD request to the URL and checking
 * if the Content-Type header starts with 'video/'.
 *
 * Note: The HEAD request will fail for cross-origin URLs due to CORS restrictions
 * in browser contexts. In such cases the function returns false. Extension-based
 * detection is the primary mechanism for client-side use.
 *
 * @param url - The URL to check if it is a video URL.
 * @returns True if the URL is a video URL, false otherwise.
 */
export async function isVideoUrl(
  url: string | null | undefined,
): Promise<boolean> {
  if (!url) return false
  if (hasVideoExtension(url)) return true
  const cached = cache.get(url)
  if (cached !== undefined) return cached
  if (!/^(https?:)\/\//.test(url)) return false

  let timeoutId
  try {
    const controller = new AbortController()
    timeoutId = setTimeout(() => controller.abort(), HEAD_REQUEST_TIMEOUT_MS)

    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
    })

    const contentType = response.headers.get('Content-Type')
    const result = contentType?.startsWith('video/') ?? false
    cache.set(url, result)
    return result
  } catch {
    cache.set(url, false)
    return false
  } finally {
    clearTimeout(timeoutId)
  }
}
